import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router, ActivatedRoute, ParamMap, NavigationExtras } from '@angular/router';
import { AppDataService } from 'src/app/services/app-data.service';
import { CoreService, DialogResult, DialogService, DocumentListFieldType, FormSummaryFieldType, SpinnerService } from 'dspx-core';
import { API_ENDPOINT_KEYS, API_ENDPOINTS } from 'src/app/constants/api-endpoints.constants';
import { TEMPLATES, VERSIONS } from 'src/app/constants/templates.constants';
import { finalize, take } from 'rxjs';
import { BaseCardSetDetailComponent } from '../base-card-set-detail-component/base-card-set-detail.component';
import { ErrorService } from 'src/app/services/error.service';
import { APPLICATION_STATUS_CODES, FORM_IDENTIFIERS, OVERVIEW_CARDSET_LAYOUT_FIELD_TYPE_REPLACEMENTS, POP_UP_CODES, QUESTION_CODES, SAVE_RESULT_STATUS } from 'src/app/constants/app-data.constants';
import { ROUTE_CONSTANTS } from 'src/app/constants/route.constants';
import { FormlyConfig } from '@ngx-formly/core';
import { VptasDocumentListFieldType } from '../document-list/document-list.type';
import { CustomFormSummaryFieldType } from '../form-summary/formsummary.type';

/**
 * VPTAS Application Details Component (Template 79)
 * 
 * This component handles the detailed view of VPTAS applications for staff portal users.
 * It provides action buttons for managing application workflow:
 * - Assign: Assign application to a staff member
 * - Accept: Accept application (move to "Ready for Approval")
 * - Ineligible: Ineligible application with reason
 * - On Hold: Put application on hold with reason
 * - Further Review Required: Send back for review (Financial Delegate only)
 * - View Invoices: Navigate to invoices page
 */
@Component({
  selector: 'app-application-detail-vptas',
  templateUrl: './application-detail-vptas.component.html',
  styleUrl: './application-detail-vptas.component.scss'
})
export class ApplicationDetailVptasComponent
  extends BaseCardSetDetailComponent
  implements OnInit, OnDestroy {
  
  fieldTypeReplacements = OVERVIEW_CARDSET_LAYOUT_FIELD_TYPE_REPLACEMENTS;
  formServiceEndpoint: string = API_ENDPOINT_KEYS.getVptasApplicationDetailsConfigAndResponse;
  
  templateId: number = TEMPLATES.vptasApplicationDetails;
  versionNo: number = VERSIONS.latest;
  model: any;
  activeDialog: string = '';
  id: string = '';
  response: any;

  selectedRowId: any;
  itemMode: string = '';
  startDate: any;

  constructor(
    protected router: Router,
    protected route: ActivatedRoute,
    protected appDataService: AppDataService,
    protected dialogService: DialogService,
    private coreService: CoreService,
    private spinnerService: SpinnerService,
    private errorService: ErrorService,
    private formlyConfig: FormlyConfig
  ) {
    super(router, appDataService, dialogService);

    this.overrideSummaryAndDocumentListWithCustomComponents();
    
    // Register application functions - both actions AND condition functions for button visibility
    // The show...Button functions need to be in applicationFunctions (not conditionFunctions) to work with condition="" attributes
    this.coreService.applicationFunctions = {
      // Action functions (called when buttons are clicked)
      home: () => this.home(),
      assign: () => this.assign(),
      tripList: () => this.tripList(),
      accept: () => this.accept(),
      ineligible: () => this.ineligible(),
      inProgress: () => this.inProgress(),
      furtherReviewRequired: () => this.furtherReviewRequired(),
      gotoInvoices: () => this.gotoInvoices(),
      gotoCentrelinkCheck: () => this.gotoCentrelinkCheck(),
      gotoSubsidies: () => this.gotoSubsidies(),
      // Button visibility condition functions (used in condition="" attributes in form template)
      showAssignButton: () => this.showAssignButton(),
      showTripListButton: () => this.showTripListButton(),
      showAcceptButton: () => this.showAcceptButton(),
      showIneligibleButton: () => this.showIneligibleButton(),
      showInProgressButton: () => this.showInProgressButton(),
      showFurtherReviewRequiredButton: () => this.showFurtherReviewRequiredButton(),
      showInvoicesButton: () => this.showInvoicesButton(),
      showGotoCentrelinkCheckButton: () => this.showGotoCentrelinkCheckButton(),
      promptToUpdateApplicationStatusAsInvalidDocument: () => this.promptToUpdateApplicationStatusAsInvalidDocument(),
      showSubsidiesButton: () => this.showSubsidiesButton(),
      showInvalidDocumentButton: () => this.showInvalidDocumentButton(),
      onFileUploadCompleted: (fileUploadEvent: any, fileData: any, documentMetadata?: any): void => {
        this.handleFileUploadCompleted(fileUploadEvent, fileData, documentMetadata);
      },
      viewApplicationDetails: () => this.viewApplicationDetails(),
      createAdjustmentApplication: () => this.createAdjustmentApplication(),
      showCreateAdjustmentApplication: () => this.showCreateAdjustmentApplication()
    };
  }
  handleFileUploadCompleted(fileUploadEvent: any, fileData: any, documentMetadata?: any) {
    if (documentMetadata) {
      this.spinnerService.showSpinner();
      this.registerSubscription(
        this.appDataService
          .post(API_ENDPOINTS.vptasApplicationDetails.uploadUpdateMetadata, documentMetadata)
          .pipe(
            take(1),
            this.errorService.errorHandler(),
            finalize(() => this.spinnerService.hideSpinner())
          )
          .subscribe(response => {
            console.log('Document metadata updated successfully:', response);
          })
      );
    }
  }
  ngOnInit(): void {

    // Subscribe to route params
    this.route.paramMap.subscribe((paramMap: ParamMap) => {
      const id = paramMap.get('id');
      if (id) {
        this.id = id;
        
        // Set up file upload form identifiers for staff document uploads      
        // that are immediately linked to this application in the FileUploadMapping table
        const formIdentifiers: any = [
          FORM_IDENTIFIERS.vptsFormIdentifier,
          this.id, // application Id (or entityId) - links documents to this specific application
        ];
        this.coreService.fileUploadFormIdentifiers[QUESTION_CODES.vptasApplicationDetailsForm.applicationDocumentPlaceholder] = formIdentifiers;
      }
    });
  }

  gotoCentrelinkCheck() {
    const url = "https://business.centrelink.gov.au/";
    window.open(url, '_blank');
  }

  ngOnDestroy(): void {
    this.revertBackToDefaultSummaryAndDocumentListComponents();
    this.unsubscribeSubscriptions();
    this.coreService.applicationFunctions = {};
    this.coreService.conditionFunctions = {};
  }

  formLoaded($event: any): void {
    this.response = $event;
    
    // Ensure button visibility flags have default values to prevent undefined errors
    if (this.response) {
      this.response.showAssignButton = this.response.showAssignButton ?? false;
      this.response.showAcceptButton = this.response.showAcceptButton ?? false;
      this.response.showIneligibleButton = this.response.showIneligibleButton ?? false;
      this.response.showFurtherReviewButton = this.response.showFurtherReviewButton ?? false;
      this.response.showGotoCentrelinkCheckButton = this.response.showGotoCentrelinkCheckButton ?? false;
      this.response.isUserAssigned = this.response.isUserAssigned ?? false;
    }
  }

  buttonClicked($event: any): void { }

  filter($event: any): void { }

  modelChanged($event: any): void { }

  save($event: any): void { }

  cancel($event: any): void {
    this.navigateBackToHome();
  }

  dialogButton_assignApplicationUserPopup_Clicked($event: any): void {
    this.assignApplicationUser($event);
  }

  dialogButton_ineligibleApplicationPopup_Clicked($event: any): void {
    this.ineligibleApplication($event);
  }

  dialogButton_inProgressPopup_Clicked($event: any): void {
    this.inProgressApplication($event);
  }

  dialogButton_furtherReviewRequiredPopup_Clicked($event: any): void {
    this.applicationFurtherReviewRequired($event);
  }

  dialogButton_invalidDocumentPopup_Clicked($event: any): void {
    this.UpdateApplicationStatusAsInvalidDocument($event);
  }

  actionButton_noteListPlaceholder_save_Clicked(event: any): void {
    this.spinnerService.showSpinner();
    this.registerSubscription(
      this.appDataService
        .post(API_ENDPOINTS.note.save, { parentEntityId: this.id, noteContent: event.field.data,addUserDetails:true })
        .pipe(
          take(1),
          this.errorService.errorHandler(),
          finalize(() => this.spinnerService.hideSpinner())
        )
        .subscribe(data => {
          if (data?.status === SAVE_RESULT_STATUS.success) {
            this.reloadPageView(true);
          }
        })
    );
  }

  actionButton_personGrid_datagrid_view_Clicked(event: any): void {
    const url = this.router.serializeUrl(this.router.createUrlTree(['/', ROUTE_CONSTANTS.person.prefixDetail, event.event.data.id]));
    window.open(url, '_blank');
  }

  actionButton_recentApplicationsGrid_datagrid_colLink_referenceNumber_Clicked(event: any): void {
    const url = this.router.serializeUrl(this.router.createUrlTree(['/', ROUTE_CONSTANTS.vptasApplication.prefix, ROUTE_CONSTANTS.vptasApplication.details.prefix, event.event.data.id]));
    window.open(url, '_blank');
  }
  actionButton_duplicateApplicationsGrid_datagrid_colLink_applicationNumber_Clicked(event: any): void {
    const url = this.router.serializeUrl(this.router.createUrlTree(['/', ROUTE_CONSTANTS.vptasApplication.prefix, ROUTE_CONSTANTS.vptasApplication.details.prefix, event.event.data.id]));
    window.open(url, '_blank');
  }

  actionButton_patientContributionsGrid_datagrid_editContribution_Clicked(event: any): void {
    this.activeDialog = POP_UP_CODES.vptasApplicationDetails.updatePatientContributionPopup;
        // Parse contribution amount to ensure it's a decimal number, removing currency symbols if present
    let contributionAmount = event.event.data.outstandingAmount;
    if (typeof contributionAmount === 'string') {
      // Remove currency symbols, commas, and whitespace, then parse as float
      contributionAmount = parseFloat(contributionAmount.replace(/[$,\s]/g, ''));
    }
    this.dialogService.showPopup(this.activeDialog, 
      { 
        id: event.event.data.id,
        updatePatientContributionStartDate: event.event.data.contributionAnniversaryStartDate,
        updatePatientContributionEndDate: event.event.data.contributionAnniversaryEndDate,
        updatePatientContributionOutstandingAmount: contributionAmount
      });
  }

  dialogButton_updatePatientContributionPopup_Clicked(event: any): void {
    // Parse contribution amount to ensure it's a valid number
    let contributionAmount = event.updatePatientContributionOutstandingAmount;
    
    // If it's a string, try to parse it
    if (typeof contributionAmount === 'string') {
      contributionAmount = parseFloat(contributionAmount.replace(/[$,\s]/g, ''));
    }
    
    // Exit if parsing failed or value is not a valid number
    if (isNaN(contributionAmount)) {
      console.error('Invalid contribution amount:', event.updatePatientContributionOutstandingAmount);
      return;
    }
    
    this.spinnerService.showSpinner();
    this.appDataService
      .post(API_ENDPOINTS.vptasNominatedPatient.updatePatientContribution,
        {
          id: event.id,
          startDate: event.updatePatientContributionStartDate,
          endDate: event.updatePatientContributionEndDate,
          contributionAmount: contributionAmount
        })
      .pipe(
        take(1),
        this.errorService.errorHandler(),
        finalize(() => this.spinnerService.hideSpinner())
      )
      .subscribe((data) => {
        this.reloadPageView(true);
      });
  }

  actionButton_adjustmentApplicationsGrid_datagrid_editAdjustmentApplication_Clicked(event: any): void {
    this.router.navigate(['/', ROUTE_CONSTANTS.vptasAdjustmentApplication.prefix, event?.event?.data?.id ]);
  }

  actionButton_adjustmentApplicationsGrid_datagrid_deleteAdjustmentApplication_Clicked(event: any): void {
    this.deleteApplication(event?.event?.data?.id);
  }

  // ==================== ACTION BUTTON METHODS ====================

  /**
   * Home - Navigate back to application list
   */
  public home($event?: any): void {
    this.navigateBackToHome();
  }

  /**
   * Assign - Open popup to assign application to a staff member
   */
  private assign(): void {
    const assignPopup = POP_UP_CODES.vptasApplicationDetails.assignApplicationUserPopup;
    const reAssignPopup = POP_UP_CODES.vptasApplicationDetails.reAssignApplicationUserPopup;

    if (this.response?.isUserAssigned !== true) {
      this.activeDialog = assignPopup;
      this.dialogService.showDialog(this.activeDialog);
      return;
    }

    this.dialogService
      .showConfirm(reAssignPopup)
      .pipe(take(1))
      .subscribe((result: DialogResult) => {
        if (result !== DialogResult.ACCEPTED) {
          return;
        }

        this.activeDialog = reAssignPopup;
        this.dialogService.showDialog(this.activeDialog);
      });
  }

  /**
   * tripList - Navigate to partially paid list page for this application
   */
  private tripList(): void {
    this.router.navigate(['/', ROUTE_CONSTANTS.vptasApplication.prefix, this.id, 'trip-list']);
  }

  private assignApplicationUser(popupQuestion: any): void {
    const payload = { id: this.id, assignedToUserId: popupQuestion?.assignedToUserIdPopup };
    this.spinnerService.showSpinner();
    this.registerSubscription(
      this.appDataService
        .post(API_ENDPOINTS.victorianPatientTransportAssistanceSchemeApplication.assignApplicationUser, payload)
        .pipe(
          take(1),
          this.errorService.errorHandler(),
          finalize(() => this.spinnerService.hideSpinner())
        )
        .subscribe(response => {
          if (response?.status === SAVE_RESULT_STATUS.success) {
            this.reloadPageView(true);
          }
        })
    );
  }

  /**
   * Accept - Accept application (move to "Ready for Approval" list)
   */
  private accept(): void {
    this.spinnerService.showSpinner();
    this.registerSubscription(
      this.appDataService
        .postAndFetchData(`${API_ENDPOINTS.victorianPatientTransportAssistanceSchemeApplication.acceptApplication}/${this.id}`)
        .pipe(
          take(1),
          this.errorService.errorHandler(),
          finalize(() => this.spinnerService.hideSpinner())
        )
        .subscribe(response => {
          if (response?.status === SAVE_RESULT_STATUS.success) {
            this.reloadPage(true);
          }
        })
    );
  }

  /**
   * Open popup to ineligible application with reason
   */
  private ineligible(): void {
    this.activeDialog = POP_UP_CODES.vptasApplicationDetails.ineligibleApplicationPopup;
    this.dialogService.showPopup(this.activeDialog);
  }

  private inProgress(): void {
    this.activeDialog = POP_UP_CODES.vptasApplicationDetails.inProgressPopup;
    this.dialogService.showPopup(this.activeDialog);
  }

  private ineligibleApplication(popupQuestion: any): void {
    const payload = { id: this.id, applicationIneligibleReasonTypeId: popupQuestion?.applicationIneligibleReasonTypeId, ineligibleOther: popupQuestion?.ineligibleOther  };
    this.spinnerService.showSpinner();
    this.registerSubscription(
      this.appDataService
        .post(API_ENDPOINTS.victorianPatientTransportAssistanceSchemeApplication.ineligibleApplication, payload)
        .pipe(
          take(1),
          this.errorService.errorHandler(),
          finalize(() => this.spinnerService.hideSpinner())
        )
        .subscribe(response => {
          if (response?.status === SAVE_RESULT_STATUS.success) {
            this.reloadPage(true);
          }
        })
    );
  }

  private inProgressApplication(popupQuestion: any): void {
    const payload = { id: this.id, inProgressReason: popupQuestion?.inProgressReason  };
    this.spinnerService.showSpinner();
    this.registerSubscription(
      this.appDataService
        .post(API_ENDPOINTS.vptasApplicationDetails.inProgressApplication, payload)
        .pipe(
          take(1),
          this.errorService.errorHandler(),
          finalize(() => this.spinnerService.hideSpinner())
        )
        .subscribe(response => {
          if (response?.status === SAVE_RESULT_STATUS.success) {
            this.reloadPageView(true);
          }
        })
    );
  }

  /**
   * Further Review Required - Send application back for review (Financial Delegate only)
   */
  private furtherReviewRequired(): void {
    this.activeDialog = POP_UP_CODES.vptasApplicationDetails.furtherReviewRequiredPopup;
    this.dialogService.showDialog(this.activeDialog);
  }

  private applicationFurtherReviewRequired(popupQuestion: any): void {
    const payload = { id: this.id, reviewReason: popupQuestion?.reviewReason };
    this.spinnerService.showSpinner();
    this.registerSubscription(
      this.appDataService
        .post(API_ENDPOINTS.victorianPatientTransportAssistanceSchemeApplication.furtherReviewRequired, payload)
        .pipe(
          take(1),
          this.errorService.errorHandler(),
          finalize(() => this.spinnerService.hideSpinner())
        )
        .subscribe(response => {
          if (response?.status === SAVE_RESULT_STATUS.success) {
            this.navigateBackToHome(true);
          }
        })
    );
  }

  private promptToUpdateApplicationStatusAsInvalidDocument(): void {
    this.activeDialog = POP_UP_CODES.vptasApplicationDetails.invalidDocumentPopup;
    this.dialogService.showDialog(this.activeDialog);
  }
  
  private UpdateApplicationStatusAsInvalidDocument(model: any): void {
    const payload = { id: this.id, invalidDocumentReasonId: model?.invalidDocumentPopupReasonId, customReason: model?.invalidDocumentPopupReasonCustom };
    this.spinnerService.showSpinner();
    this.registerSubscription(
      this.appDataService
        .post(API_ENDPOINTS.vptasApplicationDetails.updateApplicationStatusAsInvalidDocument, payload)
        .pipe(
          take(1),
          this.errorService.errorHandler(),
          finalize(() => this.spinnerService.hideSpinner())
        )
        .subscribe(response => {
          if (response?.status === SAVE_RESULT_STATUS.success) {
            this.reloadPage(true);
          }
        })
    );
  }

  /**
   * View Invoices - Navigate to invoices page for this application
   */
  private gotoInvoices(): void {
    this.router.navigate(['/', ROUTE_CONSTANTS.vptasApplication.prefix, this.id, ROUTE_CONSTANTS.vptasApplication.details.invoiceListPrefix]);
  }

  gotoSubsidies() {
    this.router.navigate(['/', ROUTE_CONSTANTS.vptasApplication.prefix, this.id, 'subsidies']);
  }
  // ==================== BUTTON VISIBILITY CONDITION METHODS ====================

  /**
   * Show Assign button - determined by backend based on status and user role
   */
  private showAssignButton(): boolean {
    return this.response?.showAssignButton === true;
  }
/**
   * Show Trip List button - determined by backend based on status and user role
   */
  private showTripListButton(): boolean {
    return this.response?.showTripListButton === true;
  }

  /**
   * Show Goto Centrelink Check button - determined by backend based on status and role
   */
  private showGotoCentrelinkCheckButton(): boolean {
    return this.response?.showGotoCentrelinkCheckButton === true;
  }

  /**
   * Show Accept button - determined by backend based on status, role, and assignment
   */
  private showAcceptButton(): boolean {
    return this.response?.showAcceptButton === true;
  }

  /**
   * Show Ineligible button - determined by backend based on status, role, and assignment
   */
  private showIneligibleButton(): boolean {
    return this.response?.showIneligibleButton === true;
  }

  private showInProgressButton(): boolean {
    return this.response?.showInProgressButton === true;
  }

  private showInvalidDocumentButton(): boolean {
    return this.response?.showInvalidDocumentButton === true;
  }
  /**
   * Show Further Review Required button - determined by backend based on status and role
   */
  private showFurtherReviewRequiredButton(): boolean {
    return this.response?.showFurtherReviewButton === true;
  }

  /**
   * Show View Invoices button - always visible
   */
  private showInvoicesButton(): boolean {
    return this.response?.showViewInvoicesButton === true;
  }
  
  private showSubsidiesButton(): boolean {
    return this.response?.showTripListButton === true;
  }
  // ==================== HELPER METHODS ====================

  /**
   * Check if application is In Progress or On Hold and assigned to current user
   */
  private isApplicationInProgressAndAssignedToMe(): boolean {
    const status = this.response?.applicationStatusCode;
    const isAssignedToMe = this.response?.applicationAssignedTo === this.response?.loggedInUserId;
    return (status === APPLICATION_STATUS_CODES.inprogress || status === APPLICATION_STATUS_CODES.onhold) && isAssignedToMe;
  }

  /**
   * Check if application is in terminal state (Rejected or Completed)
   */
  private isApplicationInTerminalState(): boolean {
    const status = this.response?.applicationStatusCode;
    return status === APPLICATION_STATUS_CODES.ineligible || status === APPLICATION_STATUS_CODES.completed;
  }

  /**
   * Reload the current page to reflect updated data
   */
  protected reloadPage(ignoreUnsavedChanges = false): void {
    this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
      this.router.navigate([this.router.url], { state: { ignoreUnsavedChanges } });
    });
  }
  
  protected reloadPageView(ignoreUnsavedChanges = false) {
    this.router.routeReuseStrategy.shouldReuseRoute = () => false;
    this.router.onSameUrlNavigation = 'reload';
    this.navigate([this.router.url], ignoreUnsavedChanges);
  }
  /**
   * Navigate back to VPTAS application list with state preservation
   */
  private navigateBackToHome(ignoreUnsavedChanges = false): void {
    const navExtras = this.getNavigationExtras();
    // Navigate to VPTAS application list - my applications
    const target = ROUTE_CONSTANTS.vptasApplication.list.myApplications;
    const segments = typeof target === 'string' ? target.replace(/^\//, '').split('/') : [target as any];
    this.navigate(['/', ...segments], ignoreUnsavedChanges, navExtras);
  }

  /**
   * Get navigation extras to preserve filter state
   */
  private getNavigationExtras(): NavigationExtras {
    return {
      state: {
        applicationFilterRequest: history.state.applicationFilterRequest
      }
    };
  }

  
  private overrideSummaryAndDocumentListWithCustomComponents() {
    this.formlyConfig.setType([
      {
        name: 'document-list',
        component: VptasDocumentListFieldType
      },
      {
        name: 'summary',
        component: CustomFormSummaryFieldType
      }
    ]);
  }

  
  private revertBackToDefaultSummaryAndDocumentListComponents() {
    this.formlyConfig.setType([
      {
        name: 'document-list',
        component: DocumentListFieldType
      },
      {
        name: 'summary',
        component: FormSummaryFieldType
      }
    ]);
  }

  private viewApplicationDetails() {
    const url = this.router.serializeUrl(this.router.createUrlTree(['/', ROUTE_CONSTANTS.victorianPatientTransportAssistanceSchemeApplication.prefix, this.id]));
    window.open(url, '_blank');
  }

    
  actionButton_tripDetailsGrid_datagrid_removeTripComponent_Clicked(event: any): void {
    this.selectedRowId=event?.event?.data?.id;
    this.itemMode =event?.event?.data?.mode;
    this.startDate =event?.event?.data?.startDate;
    this.activeDialog = POP_UP_CODES.vptasApplicationDetails.partialPaymentPopup;
    this.dialogService.showDialog(this.activeDialog);
  }

  actionButton_tripDetailsGrid_datagrid_undoRemovalOfTripComponent_Clicked(event: any): void {
    this.selectedRowId=event?.event?.data?.id;
    this.itemMode =event?.event?.data?.mode;
    this.startDate =event?.event?.data?.startDate;
    const popupCode  = POP_UP_CODES.vptasApplicationDetails.revertPartialPayment;

    this.dialogService
      .showConfirm(popupCode).subscribe(data => {
      if (data === DialogResult.ACCEPTED) {
        this.updateTreatmentItemPaymentEligibility(false, null,null);
      }
    });
         
  }

  dialogButton_partialPaymentPopup_Clicked($event: any): void {
    this.updateTreatmentItemPaymentEligibility(true, $event?.applicationIneligibleReasonTypeId,$event.otherReason);
  }

  private updateTreatmentItemPaymentEligibility(isPartialPaymentExcluded: boolean, applicationIneligibleReasonTypeId: any, partialPaymentOther: any): void {
    const payload = { 
      id: this.selectedRowId,
      applicationId: this.id,
      isPartialPaymentExcluded: isPartialPaymentExcluded,
      tripMode: this.itemMode,
      partialPaymentReason: applicationIneligibleReasonTypeId,
      partialPaymentOtherReason: partialPaymentOther,
      tripDate: this.startDate
    };
    
    this.spinnerService.showSpinner();
    this.registerSubscription(
      this.appDataService
        .post(API_ENDPOINTS.vptasApplicationDetails.updateTreatmentItemPaymentEligibility, payload)
        .pipe(
          take(1),
          this.errorService.errorHandler(),
          finalize(() => this.spinnerService.hideSpinner())
        )
        .subscribe(response => {
          this.reloadPageView(true);
        })
    );
  }

  actionButton_subsidyGrid_datagrid_createAdjustment_Clicked(event: any): void {
    this.activeDialog = POP_UP_CODES.vptasApplicationSubsidyList.addAdjustment;
    this.dialogService.showPopup(this.activeDialog, { id: event.event.data.id });
  }

  dialogButton_addAdjustment_Clicked(event: any): void {
    const subsidyAdjustmentAmount = this.parseDecimalAmount(event.subsidyAdjustmentAmount);

    if (subsidyAdjustmentAmount === null) {
      this.dialogService.showPopup(POP_UP_CODES.vptasApplicationSubsidyList.addAdjustmentDecimalWarning, null);
    }
    else {
      this.spinnerService.showSpinner();
      this.appDataService
        .post(API_ENDPOINTS.vptasApplicationDetails.addAdjustmentToSubsidy,
          {
            id: event.id,
            subsidyAdjustedAmount: subsidyAdjustmentAmount,
            adjustmentReason: event.adjustmentReason
          })
        .pipe(
          take(1),
          this.errorService.errorHandler(),
          finalize(() => this.spinnerService.hideSpinner())
        )
        .subscribe((data) => {
          this.reloadPageView();
        });
    }
  }

  private parseDecimalAmount(value: unknown): number | null {
    if (typeof value === 'number') {
      return Number.isFinite(value) ? value : null;
    }

    if (typeof value !== 'string') {
      return null;
    }

    const sanitisedValue = value.trim().replace(/[$,\s]/g, '');

    if (!/^-?\d+(\.\d+)?$/.test(sanitisedValue)) {
      return null;
    }

    const parsedValue = parseFloat(sanitisedValue);
    return Number.isFinite(parsedValue) ? parsedValue : null;
  }

  private createAdjustmentApplication() {
    this.router.navigate(['/', this.id, ROUTE_CONSTANTS.vptasAdjustmentApplication.prefix]);
  }

  private showCreateAdjustmentApplication() {
    return this.response.showCreateAdjustmentApplication ?? false;
  }

  private deleteApplication(id) {
    this.dialogService
      .showConfirm(POP_UP_CODES.vptasApplicationList.deleteApplicationConfirmation)
      .pipe(take(1))
      .subscribe(data => {
        switch (data as DialogResult) {
          case DialogResult.ACCEPTED: {
            this.spinnerService.showSpinner();
            this.appDataService
            .postAndFetchData(`${API_ENDPOINTS.vptasApplication.delete}/${id}`)
              .pipe(take(1), this.errorService.errorHandler(),
                finalize(() => this.spinnerService.hideSpinner))
              .subscribe(response => {
                this.reloadPageView();
              });
            break;
          }
          case DialogResult.REJECTED: {
            break;
          }
        }
      });
  }
}
